import { Controller, useForm } from 'react-hook-form';

import { faMobile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, FormLabel, Stack, Typography } from '@mui/material';
import { PhoneInput } from '@sage/ui-components';

import parsePhoneNumber from 'libphonenumber-js/max';
import DrawerBody from 'src/components/drawers/DrawerBody';
import DrawerFooter from 'src/components/drawers/DrawerFooter';
import {
    getPhoneNumberValidationRules,
    limitPhoneNumberLength,
} from 'src/utils/phoneNumberInputUtils';

interface InputPhoneNumberProps {
    onNext: (phoneNumber: string) => Promise<void>;
    onCancel: () => void;
    defaultPhoneNumber?: string;
}

interface FormValues {
    phoneNumber: string;
}

export const InputPhoneNumber = (props: InputPhoneNumberProps) => {
    const { onNext, onCancel, defaultPhoneNumber = '' } = props;

    const {
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<FormValues>({
        defaultValues: { phoneNumber: defaultPhoneNumber },
    });

    const handleContinue = handleSubmit(async ({ phoneNumber }) => {
        const cleaned = parsePhoneNumber(phoneNumber)?.number;
        if (cleaned) {
            await onNext(cleaned);
        }
    });

    return (
        <form onSubmit={handleContinue}>
            <DrawerBody sx={{ alignItems: 'flex-start', gap: 2, width: '100%' }}>
                <FontAwesomeIcon icon={faMobile} size="2x" />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    Using personal phone
                </Typography>
                <Typography variant="body1">
                    Enter your phone number to receive shift alerts via SMS.
                </Typography>
                <Stack sx={{ gap: 0.5, alignSelf: 'stretch' }}>
                    <FormLabel required htmlFor="phone-number-input">
                        Phone Number
                    </FormLabel>
                    <Controller
                        name="phoneNumber"
                        control={control}
                        rules={getPhoneNumberValidationRules({ isRequired: true })}
                        render={({
                            field: { onChange, value, ref, ...fieldProps },
                            fieldState,
                        }) => (
                            <PhoneInput
                                {...fieldProps}
                                id="phone-number-input"
                                fullWidth
                                value={value}
                                onChange={(val) => onChange(limitPhoneNumberLength(val))}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                </Stack>
                <Typography variant="body1">
                    By continuing, you agree to receive a 6-digit verification code and shift alerts
                    by text.
                </Typography>
            </DrawerBody>
            <DrawerFooter sx={{ gap: 1.5, width: '100%' }}>
                <Button
                    fullWidth
                    disableElevation
                    color="primary"
                    variant="contained"
                    type="submit"
                    loading={isSubmitting}
                >
                    Continue
                </Button>
                <Button fullWidth variant="text" onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                </Button>
            </DrawerFooter>
        </form>
    );
};
